<script setup lang="ts">
import type { IBookingServicesQuery } from '@types'
import type { CalendarPeriod } from '@ui/components/calendar/picker'
import CompanyRulesContent from '@booking/components/CompanyRulesContent.vue'
import InventoryCatalog from '@booking/components/inventory/InventoryCatalog.vue'
import { useInventoryTimezone } from '@booking/composables/useInventoryTimezone'
import { useBookingCartStore } from '@booking/stores/bookingCart'
import { useCompanyStore } from '@booking/stores/company'
import { getCompanyImages } from '@booking/utils/images'
import { getNextInventoryStart, isInventoryPeriodValid } from '@booking/utils/inventoryPeriod'
import { joinPageTitle, usePageTitle } from '@booking/utils/usePageSettings'
import ServiceListCard from '@sub/components/ServiceListCard.vue'
import { serializeBookingCartQuery } from '@sub/utils/bookingCartQuery'
import { RulesTypes, ServiceTypes } from '@types'
import { getCategoryLocale } from '@utils/categoryUtils'
import { Feature, hasFeatureAccess } from '@utils/features.ts'
import { useDebounceFn } from '@vueuse/core'
import { addDays } from 'date-fns'
import Envelope from '~icons/cue-regular/envelope'
import Phone from '~icons/cue-regular/phone'
import Search from '~icons/cue-regular/search'

const store = useCompanyStore()
const bookingCart = useBookingCartStore()
const route = useRoute()
const { company, companyInfo, hasBookableInventoryServices, inventoryServices, serviceCategories, services } =
  storeToRefs(store)
const inventoryEnabled = computed(
  () => hasFeatureAccess(company.value, Feature.Inventory) && hasBookableInventoryServices.value,
)

const inventoryTimezone = useInventoryTimezone()
const initialInventoryStart = getNextInventoryStart(inventoryTimezone.value)
const inventoryPeriod = ref<CalendarPeriod>({
  start: bookingCart.period ? new Date(bookingCart.period.startAt) : initialInventoryStart,
  end: bookingCart.period ? new Date(bookingCart.period.endAt) : addDays(initialInventoryStart, 1),
})
const inventoryCategoryId = ref<string | number>('')
const inventorySearch = ref('')

function getInventoryServicesQuery(): IBookingServicesQuery {
  const search = inventorySearch.value.trim()
  const categoryId = Number(inventoryCategoryId.value)

  return {
    type: ServiceTypes.Inventory,
    startAt: inventoryPeriod.value.start.toISOString(),
    endAt: inventoryPeriod.value.end.toISOString(),
    ...(categoryId > 0 ? { categoryId } : {}),
    ...(search.length >= 3 ? { search } : {}),
  }
}

usePageTitle(computed(() => joinPageTitle(company.value.clientName, $t('page.services.title'))))

const { status } = await useAsyncData('services-page', async () => {
  const companyId = await store.getCompanyIdByAlias()
  if (!companyId) {
    throw showError({ statusCode: 404 })
  }

  await Promise.all([store.fetchCompany(companyId), store.fetchServices(companyId)])

  if (inventoryEnabled.value) {
    const saved = bookingCart.period
      ? { start: new Date(bookingCart.period.startAt), end: new Date(bookingCart.period.endAt) }
      : undefined
    if (saved && isInventoryPeriodValid(saved)) {
      inventoryPeriod.value = saved
    } else {
      const start = getNextInventoryStart(inventoryTimezone.value)
      inventoryPeriod.value = { start, end: addDays(start, 1) }
    }
    if (route.query.section === 'inventory') {
      await Promise.all([
        store.fetchServiceCategories(companyId),
        store.fetchInventoryServices(companyInfo.value.alias, getInventoryServicesQuery()),
      ])
    }
  }

  return true
})

const showRules = ref(false)

const CatalogSections = {
  Services: 'services',
  Inventory: 'inventory',
} as const

type CatalogSection = (typeof CatalogSections)[keyof typeof CatalogSections]

const catalogSection = computed<CatalogSection>({
  get: () =>
    inventoryEnabled.value && route.query.section === CatalogSections.Inventory
      ? CatalogSections.Inventory
      : CatalogSections.Services,
  set: section => {
    void navigateTo({
      path: route.path,
      query: {
        ...route.query,
        section: section === CatalogSections.Inventory ? CatalogSections.Inventory : undefined,
      },
      hash: route.hash,
    })
  },
})
const catalogSections = [
  { id: CatalogSections.Services, label: 'page.services.sections.services' },
  { id: CatalogSections.Inventory, label: 'page.services.sections.inventory' },
]

const inventoryMobileSearchOpen = ref(false)
const inventorySearchInput = ref<{ focus: () => void }>()

const refreshInventoryServices = useDebounceFn(async () => {
  if (catalogSection.value === CatalogSections.Inventory && companyInfo.value.alias) {
    await Promise.all([
      store.fetchServiceCategories(companyInfo.value.id),
      store.fetchInventoryServices(companyInfo.value.alias, getInventoryServicesQuery()),
    ]).catch(() => {})
  }
}, 300)

watch([inventoryPeriod, inventoryCategoryId, inventorySearch], () => refreshInventoryServices(), { deep: true })

async function openInventorySearch() {
  inventoryMobileSearchOpen.value = true
  await nextTick()
  inventorySearchInput.value?.focus()
}

function closeInventorySearch() {
  inventoryMobileSearchOpen.value = false
  inventorySearch.value = ''
}

watch(catalogSection, section => {
  if (section !== CatalogSections.Inventory) {
    closeInventorySearch()
  } else {
    if (!isInventoryPeriodValid(inventoryPeriod.value)) {
      const start = getNextInventoryStart(inventoryTimezone.value)
      inventoryPeriod.value = { start, end: addDays(start, 1) }
    }
    refreshInventoryServices()
  }
})

const selectedAddresses = ref<number[]>([])

const filteredServices = computed(() =>
  services.value.filter(s => {
    return selectedAddresses.value.length
      ? (s.addresses || []).some(({ id }) => selectedAddresses.value.includes(+id))
      : true
  }),
)

const addresses = computed(() => company.value.addresses)
const companyImages = computed(() => getCompanyImages(company.value))

const showViewer = ref(false)
const activeImages = ref<string[]>([])

function onOpenViewer(images: string[], startIndex = 0) {
  activeImages.value = [...images.slice(startIndex), ...images.slice(0, startIndex)]
  showViewer.value = true
}

const seoImageUrl = computed(() => companyImages.value[0] || company.value.logoUrl)

useSeoMeta({
  description: company.value.clientDescription,
  ogTitle: company.value.clientName,
  ogDescription: company.value.clientDescription,
  ogImage: seoImageUrl,
  twitterTitle: company.value.clientName,
  twitterDescription: company.value.clientDescription,
  twitterImage: seoImageUrl,
  twitterCard: 'summary',
})

definePageMeta({
  keepalive: true,
})
</script>

<template lang="pug">
UiLoaderCircleLoader.m-auto(v-if="status === 'pending'" label="page.services.loader")
template(v-else-if="company && Object.keys(company).length")
  .company-info.overflow-hidden
    .company-text.lt-md_d-contents(v-if="company && Object.keys(company).length")
      .company-header.flex.row-16.align-items-center.w-100p.o-0
        UiImage.company-logo.s-48(class="md_s-56" :src="company.logoUrl")
        .company-name.flex.col-0.min-w-0
          .title.typ-20-28-600(class="md_typ-22-24-600") {{ company.clientName }}
          .category.typ-13-17-400(v-if="company.meta?.category" class="md_typ-14-20-400") {{ $t(getCategoryLocale(company.meta?.category)) }}
      .company-details.flex.col-12.o-2(v-if="company.meta?.phone || company.meta?.email || company.rules || company.clientDescription")
        .contact.flex.row-10.align-items-center(v-if="company.meta?.phone")
          UiIcon.icon.s-18(:name="Phone")
          a.title.typ-14-20-400(:href="`tel:${company.meta.phone}`") {{ company.meta.phone }}
        .contact.flex.row-10.align-items-center(v-if="company.meta?.email")
          UiIcon.icon.s-18(:name="Envelope")
          a.title.typ-14-20-400(:href="`mailto:${company.meta.email}`") {{ company.meta.email }}
        span.link.typ-14-20-500(v-if="company.rules" @click="showRules = true") {{ $t('block.term-of-service.title') }}
        span.description.typ-14-20-400.text-pre-wrap(v-if="company.clientDescription") {{ company.clientDescription }}
    template(v-if="companyImages.length")
      UiCarousel.image-carousel.o-1.md_d-none
        UiImage.s-292(v-for="image in companyImages" :key="image" :src="image" :alt="company.clientName")
      UiImage.company-image.lt-md_d-none.s-216.br-20(:src="companyImages[0]" :zoom="true" @click="onOpenViewer(companyImages)")

  .services.flex
    .catalog-switcher.flex.w-100p(v-if="inventoryEnabled")
      UiInput.mobile-inventory-search(
        v-if="catalogSection === CatalogSections.Inventory && inventoryMobileSearchOpen && isLtMd"
        ref="inventorySearchInput"
        v-model="inventorySearch"
        placeholder="page.services.inventory.search"
        :iconRight="Search"
        :size="Sizes.HanSolo"
        :background="Background.White"
        @click:rightIcon="closeInventorySearch"
      )
      template(v-else)
        UiChipGroup.flex-1(
          v-model="catalogSection"
          :options="catalogSections"
          :size="Sizes.HanSolo"
        )
        UiIconButton.mobile.inventory-search-button(
          v-if="catalogSection === CatalogSections.Inventory"
          :name="Search"
          :variant="ButtonVariant.Ghost"
          :size="Sizes.HanSolo"
          @click="openInventorySearch"
        )
    .p-b-16(v-if="false && addresses.length > 1")
      UiInputSelect(
        v-model="selectedAddresses"
        labelProp="country"
        placeholder="block.address-select.button.text.default"
        :background="Background.Outline"
        :size="Sizes.HanSolo"
        :options="addresses"
        :multiple="true"
      )
    .services-list.flex.col-20(v-if="catalogSection === CatalogSections.Services")
      template(v-for="(s, index) of filteredServices" :key="s.id")
        UiDivider(v-if="index" variant="light" :size="Sizes.R2D2")

        ServiceListCard(
          :service="s"
          :showServiceDuration="companyInfo.settings.showServiceDuration"
          :showServicePrice="companyInfo.settings.showMinServicePrice"
          :defaultImage="company.logoUrl"
          @open:images="onOpenViewer"
        )
    InventoryCatalog(
      v-else-if="catalogSection === CatalogSections.Inventory"
      v-model:search="inventorySearch"
      v-model:period="inventoryPeriod"
      v-model:categoryId="inventoryCategoryId"
      :services="inventoryServices"
      :timezone="inventoryTimezone"
      :categories="serviceCategories"
      :defaultImage="company.logoUrl"
      :checkoutTo="{ path: '/booking/checkout', query: serializeBookingCartQuery(inventoryPeriod, bookingCart.inventoryItems) }"
    )

UiSheetBottom(v-model:show="showRules" label="block.term-of-service.title" backdropVShow)
  template(#content)
    CompanyRulesContent(:text="company[RulesTypes.Rule] || ''")

UiImageViewer(v-model:show="showViewer" :src="activeImages")
</template>

<style scoped>
.company-info {
  border-radius: 24px 24px 0 0;
  --uno: flex col-24 p-24-16-0;

  @screen md {
    border-bottom: 1px solid var(--cue-border-neutral-primary);
    --uno: flex-between-start p-24;
    flex-flow: row;
  }
}

.mobile {
  @screen md {
    display: none;
  }
}

.image-carousel {
  width: calc(100% + 32px);
  --uno: m-x-m-16 p-x-16;
}

.company-text {
  @screen md {
    --uno: flex w-456 col-0 flex-shrink-0;
  }
}

.company-details {
  @screen md {
    --uno: p-24-0-24-16;
  }
}

.company-logo {
  border: 1px solid var(--cue-border-neutral-secondary);
}

.title {
  color: var(--cue-content-neutral-primary);
  text-decoration: none;
}

.category {
  color: var(--cue-content-neutral-secondary);
}

.description {
  color: var(--cue-content-neutral-secondary);
}

.link {
  cursor: pointer;
  color: var(--cue-content-primary-default);
}

.icon {
  color: var(--cue-content-neutral-disabled);
}

.services {
  --uno: col-24 p-24-16-48;

  @screen md {
    --uno: p-16-24 col-20;
  }
}

.catalog-switcher {
  --uno: row-16;
}
</style>
